import { QuickAddComponent } from '@theme/quick-add';
import { DialogCloseEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

let activeSession = null;
let openingComponent = null;

class JDHolidayQuickAdd extends QuickAddComponent {
  #session = null;
  #prepared = false;

  constructor() {
    super();
    const nativeHandleClick = this.handleClick;
    this.handleClick = async (event) => {
      event.preventDefault();
      if (openingComponent || this.#session) return;
      activeSession?.dispose();
      openingComponent = this;
      this.#prepared = false;
      const trigger = event.target.closest('button');
      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
      }
      try {
        await nativeHandleClick(event);
        if (this.#prepared) this.#session?.synchronize();
      } catch (error) {
        this.#session?.dispose();
        const status = this.querySelector('[data-jdh-quick-add-status]');
        if (status) status.textContent = this.dataset.errorText || 'Could not open options. Please use Product details.';
        console.warn('[holiday-quick-add] Could not load product options', error);
      } finally {
        openingComponent = null;
        if (trigger) {
          trigger.disabled = false;
          trigger.removeAttribute('aria-busy');
        }
      }
    };
  }

  async fetchProductPage(url) {
    const isOpening = openingComponent === this;
    try {
      const html = await super.fetchProductPage(url);
      if (isOpening && !html?.querySelector('[data-product-grid-content]')) throw new Error('Product options were not returned');
      return html;
    } catch (error) {
      if (isOpening) throw error;
      return null;
    }
  }

  async updateQuickAddModal(productGrid) {
    await super.updateQuickAddModal(productGrid);
    const dialog = document.getElementById('quick-add-dialog');
    const content = document.getElementById('quick-add-modal-content');
    if (!dialog || !content) throw new Error('Native quick-add dialog is unavailable');
    activeSession?.dispose();
    this.#session = this.#createSession(dialog, content);
    activeSession = this.#session;
    this.#prepared = true;
  }

  disconnectedCallback() {
    this.#session?.dispose();
    super.disconnectedCallback();
  }

  #createSession(dialog, content) {
    const controller = new AbortController();
    const { signal } = controller;
    const chosen = new Map();
    const required = new Set();
    let generation = 0;
    let resolved = null;
    let active = true;
    let ready = false;
    let cartPending = false;
    let synchronizing = false;
    let timer;
    const status = document.createElement('p');
    status.className = 'jdh-quick-add-choice-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const groups = () => {
      const result = [];
      for (const picker of content.querySelectorAll('variant-picker')) {
        for (const fieldset of picker.querySelectorAll('fieldset')) {
          const inputs = [...fieldset.querySelectorAll('input[type="radio"][data-option-value-id]')];
          if (inputs.length < 2) continue;
          result.push({ key: inputs[0].dataset.optionName || inputs[0].name, inputs, fieldset });
        }
        for (const select of picker.querySelectorAll('select')) {
          const inputs = [...select.options].filter((option) => option.dataset.optionValueId);
          if (inputs.length < 2) continue;
          result.push({ key: inputs[0].dataset.optionName || select.name, inputs, select });
        }
      }
      return result;
    };

    const setDisabled = (element, disabled) => {
      if (element.disabled !== disabled) element.disabled = disabled;
    };

    const synchronize = () => {
      if (!active || synchronizing) return;
      synchronizing = true;
      try {
        const currentGroups = groups();
        for (const group of currentGroups) {
          required.add(group.key);
          const selection = chosen.get(group.key);
          const match = group.inputs.find((input) => input.dataset.optionValueId === selection?.id);
          if (selection && !match) chosen.delete(group.key);
          for (const input of group.inputs) {
            const selected = Boolean(match && input === match);
            if (group.select) {
              if (input.selected !== selected) input.selected = selected;
              if (selected && !input.hasAttribute('selected')) input.setAttribute('selected', 'selected');
              if (!selected && input.hasAttribute('selected')) input.removeAttribute('selected');
            } else {
              if (input.checked !== selected) input.checked = selected;
              if (!selected && input.hasAttribute('checked')) input.removeAttribute('checked');
              if (input.dataset.currentChecked !== String(selected)) input.dataset.currentChecked = String(selected);
              if (!selected && input.dataset.previousChecked !== 'false') input.dataset.previousChecked = 'false';
            }
          }
          if (group.select) {
            let placeholder = group.select.querySelector('[data-jdh-placeholder]');
            if (!placeholder) {
              placeholder = document.createElement('option');
              placeholder.value = '';
              placeholder.disabled = true;
              placeholder.dataset.jdhPlaceholder = 'true';
              placeholder.textContent = this.dataset.chooseText || 'Choose an option';
              group.select.prepend(placeholder);
            }
            if (!match) group.select.selectedIndex = 0;
          } else {
            const swatchLabel = group.fieldset.querySelector('legend .variant-option__swatch-value');
            if (swatchLabel && swatchLabel.textContent !== (match?.value || '')) swatchLabel.textContent = match?.value || '';
          }
        }
        const complete = required.size > 0 && [...required].every((key) => chosen.has(key));
        const resource = resolved?.resource;
        const matches = complete && resource?.available === true && [...chosen.values()].every((choice) =>
          resource.options?.[choice.position - 1] === choice.value
        );
        ready = Boolean(!cartPending && matches && resolved?.nativeButtonAvailable && resolved?.nativeInputAvailable);
        const release = ready && !resolved.released;
        if (!status.isConnected) content.prepend(status);
        const message = cartPending ? '' : ready ? '' : complete && resolved ?
          (this.dataset.unavailableText || 'This combination is unavailable. Choose another option.') :
          (this.dataset.chooseAllText || 'Choose every option before adding to bag.');
        if (status.textContent !== message) status.textContent = message;
        if (status.hidden !== (ready || cartPending)) status.hidden = ready || cartPending;
        for (const form of content.querySelectorAll('product-form-component')) {
          for (const input of form.querySelectorAll('input[name="id"]')) {
            if (!ready) setDisabled(input, true);
            else if (release) setDisabled(input, false);
          }
          for (const button of form.querySelectorAll('[ref="addToCartButton"], button[type="submit"], input[type="submit"]')) {
            if (!ready) setDisabled(button, true);
            else if (release && button.matches('[ref="addToCartButton"]')) setDisabled(button, false);
          }
          for (const checkout of form.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"], .shopify-payment-button')) {
            if (!checkout.hidden) checkout.hidden = true;
          }
        }
        if (release) resolved.released = true;
      } finally {
        synchronizing = false;
      }
    };

    const onChange = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('variant-picker')) return;
      const option = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
      if (!option?.dataset.optionValueId) return;
      const group = groups().find((item) => item.inputs.includes(option));
      if (!group) return;
      chosen.set(group.key, {
        id: option.dataset.optionValueId,
        value: option.value,
        position: Number(option.dataset.inputId?.split('-')[0]),
      });
      generation += 1;
      resolved = null;
      synchronize();
    };

    const onProductSelect = (event) => {
      if (!(event.target instanceof Element) || !content.contains(event.target) || !event.target.closest('variant-picker')) return;
      const currentGeneration = generation;
      resolved = null;
      synchronize();
      event.promise?.then(({ detail }) => {
        if (!active || currentGeneration !== generation) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (!active || currentGeneration !== generation) return;
          const nativeForm = detail?.html?.querySelector('product-form-component');
          const nativeButton = nativeForm?.querySelector('[ref="addToCartButton"]');
          const nativeInput = nativeForm?.querySelector('input[name="id"]');
          resolved = {
            resource: detail?.resource,
            nativeButtonAvailable: Boolean(nativeButton && !nativeButton.disabled),
            nativeInputAvailable: Boolean(nativeInput && !nativeInput.disabled),
          };
          synchronize();
        }, 0);
      }).catch(() => {
        if (active && currentGeneration === generation) {
          resolved = null;
          synchronize();
        }
      });
    };

    const blockPrematureAdd = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('product-form-component')) return;
      if (event.type !== 'submit' && !target.closest('[ref="addToCartButton"], button[type="submit"], input[type="submit"], .shopify-payment-button')) return;
      if (!ready || cartPending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        synchronize();
      }
    };

    const observer = new MutationObserver(synchronize);
    const session = {
      synchronize,
      dispose: (event) => {
        if (!active) return;
        active = false;
        clearTimeout(timer);
        controller.abort();
        observer.disconnect();
        status.remove();
        if (activeSession === session) activeSession = null;
        if (this.#session === session) this.#session = null;
        if (event?.type === DialogCloseEvent.eventName && !cartPending && this.isConnected) {
          const trigger = this.querySelector('button[aria-haspopup="dialog"]');
          requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
        }
      },
    };
    dialog.addEventListener('change', onChange, { capture: true, signal });
    dialog.addEventListener(StandardEvents.productSelect, onProductSelect, { capture: true, signal });
    dialog.addEventListener('submit', blockPrematureAdd, { capture: true, signal });
    dialog.addEventListener('click', blockPrematureAdd, { capture: true, signal });
    dialog.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (!(event.target instanceof Element) || !content.contains(event.target) || !event.target.closest('product-form-component')) return;
      cartPending = true;
      queueMicrotask(synchronize);
      event.promise?.then(({ detail }) => {
        if (!active) return;
        if (detail?.didError) {
          cartPending = false;
          resolved = null;
          chosen.clear();
          synchronize();
        }
      }).catch(() => {
        if (!active) return;
        cartPending = false;
        resolved = null;
        chosen.clear();
        synchronize();
      });
    }, { capture: true, signal });
    dialog.addEventListener(DialogCloseEvent.eventName, session.dispose, { signal });
    document.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('quick-add-component') && !this.contains(event.target)) session.dispose();
    }, { capture: true, signal });
    observer.observe(content, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'checked', 'selected', 'hidden'] });
    synchronize();
    return session;
  }
}

if (!customElements.get('jd-holiday-quick-add')) {
  customElements.define('jd-holiday-quick-add', JDHolidayQuickAdd);
}
